import { useState } from "react";
import { motion } from "framer-motion";
import { DeviceMobile, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { api, formatApiError } from "../lib/api";

const HIGHLIGHTS = [
  "Your consultation thread, always in your pocket",
  "The chapters, decision tool and calculator — rebuilt for mobile",
  "A document checklist for every offer and contract you're weighing",
  "First to know when new guidance publishes",
];

function PhoneMockup() {
  return (
    <motion.div
      animate={{ y: [0, -14, 0] }}
      transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
      className="relative mx-auto w-[240px] md:w-[280px]"
      data-testid="app-phone-mockup"
    >
      <div className="rounded-[2.5rem] border-[10px] border-primary bg-background overflow-hidden aspect-[9/19]">
        <div className="bg-primary px-5 pt-6 pb-5">
          <div className="mx-auto w-16 h-1.5 bg-primary-foreground/20 rounded-full mb-5" />
          <p className="font-serif text-primary-foreground text-lg leading-tight">
            The Timeshare <em className="text-accent">Edit</em>
          </p>
          <p className="text-primary-foreground/50 text-[10px] font-sans font-bold uppercase tracking-[0.2em] mt-1.5">
            Companion app
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-card border border-border rounded-sm p-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-accent">Your thread</p>
            <p className="text-[11px] font-semibold text-foreground/80 mt-1 leading-snug">"Your written answer is ready to read."</p>
          </div>
          {["01 · Buying More", "02 · Selling & Exiting", "03 · Choosing a Company", "04 · Red Flags"].map((t) => (
            <div key={t} className="flex items-center justify-between bg-card border border-border rounded-sm px-3 py-2.5">
              <p className="text-[11px] font-semibold text-foreground/75">{t}</p>
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            </div>
          ))}
          <div className="bg-primary rounded-sm p-3 text-center">
            <p className="text-[10px] font-bold text-primary-foreground uppercase tracking-[0.2em]">Cost per night: $214</p>
          </div>
        </div>
      </div>
      <div className="absolute -inset-6 -z-10 bg-accent/10 rounded-[3.5rem] rotate-3" aria-hidden />
    </motion.div>
  );
}

export default function AppSection() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/app-waitlist", { email });
      setJoined(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="app" data-testid="app-section" className="py-24 md:py-40 bg-secondary/40 border-t border-border overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-center">
        <Reveal className="lg:col-span-7">
          <Eyebrow>Coming Soon</Eyebrow>
          <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.08]" data-testid="app-heading">
            The Timeshare Edit, <em className="text-accent">in your pocket</em>
          </h2>
          <p className="mt-6 text-foreground/70 max-w-xl">
            We're building the companion app: everything on this site — and your ongoing consultation
            thread — carried with you. For the presentation room, the kitchen table, and the moment
            a "limited-time offer" lands in your inbox.
          </p>
          <ul className="mt-8 space-y-4 max-w-xl">
            {HIGHLIGHTS.map((h, i) => (
              <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground/80 font-medium">
                <DeviceMobile size={19} weight="duotone" className="text-accent shrink-0 mt-0.5" />
                {h}
              </li>
            ))}
          </ul>

          {joined ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-10 flex items-center gap-4 bg-card border border-border rounded-sm px-7 py-5 max-w-md"
              data-testid="app-waitlist-success"
            >
              <CheckCircle size={28} weight="fill" className="text-primary shrink-0" />
              <p className="text-sm font-semibold text-foreground/80">
                You're on the launch list — we'll email you the day the app arrives.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md" data-testid="app-waitlist-form">
              <input
                data-testid="app-waitlist-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-card border border-border rounded-sm px-4 py-3.5 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300"
              />
              <button
                data-testid="app-waitlist-submit"
                type="submit"
                disabled={sending}
                className="group flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300 shrink-0"
              >
                {sending ? "Joining…" : "Notify me at launch"}
                <PaperPlaneRight size={15} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          )}
          <p className="mt-4 text-xs text-foreground/45">One email at launch. Nothing else, ever.</p>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-5">
          <PhoneMockup />
        </Reveal>
      </div>
    </section>
  );
}
